// MUI theme — ZBS huisstijl, afgeleid uit zbatterysolutions.com (donker-groen
// hero, warm oranje accent, Montserrat-typografie, pill-shaped CTAs).
//
// Kleur-overrides via env vars (VITE_ZBS_PRIMARY/SECONDARY) blijven mogelijk
// zodat marketing kan bijsturen zonder re-deploy.

import { createTheme, responsiveFontSizes } from '@mui/material/styles';

// Olijf-donkergroen van de ZBS website hero + oranje accent uit de EMS-pagina.
const ZBS_PRIMARY = import.meta.env.VITE_ZBS_PRIMARY || '#1b3d2a';
const ZBS_PRIMARY_DARK = '#0f2a1c';
const ZBS_PRIMARY_LIGHT = '#2d5a3f';
const ZBS_SECONDARY = import.meta.env.VITE_ZBS_SECONDARY || '#e68e2d';
const ZBS_SECONDARY_LIGHT = '#f4a73a';
const ZBS_SECONDARY_DARK = '#c47520';

let theme = createTheme({
    palette: {
        mode: 'light',
        primary: {
            main: ZBS_PRIMARY,
            light: ZBS_PRIMARY_LIGHT,
            dark: ZBS_PRIMARY_DARK,
            contrastText: '#ffffff',
        },
        secondary: {
            main: ZBS_SECONDARY,
            light: ZBS_SECONDARY_LIGHT,
            dark: ZBS_SECONDARY_DARK,
            contrastText: '#ffffff',
        },
        success: { main: '#2e7d32' },
        warning: { main: '#ed6c02' },
        error: { main: '#c62828' },
        background: {
            default: '#f6f5f0', // warm off-white, past bij premium feel
            paper: '#ffffff',
        },
        text: {
            primary: '#162a1f',
            secondary: '#5a6b63',
        },
        divider: 'rgba(27, 61, 42, 0.12)',
    },
    typography: {
        fontFamily: '"Montserrat", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
        h1: { fontWeight: 700, fontSize: '2rem', letterSpacing: '-0.01em' },
        h2: { fontWeight: 700, fontSize: '1.65rem', letterSpacing: '-0.01em' },
        h3: { fontWeight: 600, fontSize: '1.35rem', letterSpacing: '-0.005em' },
        h4: { fontWeight: 600, fontSize: '1.15rem' },
        h5: { fontWeight: 600, fontSize: '1rem' },
        h6: { fontWeight: 600, fontSize: '0.95rem', letterSpacing: '0.02em' },
        subtitle1: { fontWeight: 500 },
        subtitle2: { fontWeight: 600, letterSpacing: '0.01em' },
        body1: { fontWeight: 400 },
        body2: { fontWeight: 400 },
        button: { textTransform: 'none', fontWeight: 600, letterSpacing: '0.01em' },
        caption: { letterSpacing: '0.02em' },
        overline: { fontWeight: 600, letterSpacing: '0.1em' },
    },
    shape: {
        borderRadius: 12,
    },
    components: {
        MuiCssBaseline: {
            styleOverrides: {
                body: {
                    WebkitFontSmoothing: 'antialiased',
                    MozOsxFontSmoothing: 'grayscale',
                },
            },
        },
        MuiButton: {
            defaultProps: { disableElevation: true },
            styleOverrides: {
                root: {
                    minHeight: 44,
                    paddingLeft: 20,
                    paddingRight: 20,
                    borderRadius: 999, // pill-shaped CTA, zoals op zbatterysolutions.com
                },
                sizeLarge: { minHeight: 52 },
                containedPrimary: ({ theme: t }) => ({
                    boxShadow: `0 1px 0 ${t.palette.primary.dark}`,
                    '&:hover': { backgroundColor: t.palette.primary.light },
                }),
                containedSecondary: ({ theme: t }) => ({
                    boxShadow: `0 1px 0 ${t.palette.secondary.dark}`,
                    '&:hover': { backgroundColor: t.palette.secondary.light },
                }),
            },
        },
        MuiIconButton: {
            styleOverrides: {
                root: { minWidth: 44, minHeight: 44 },
            },
        },
        MuiTextField: {
            defaultProps: { variant: 'outlined', fullWidth: true, size: 'medium' },
        },
        MuiFormControl: {
            defaultProps: { fullWidth: true, size: 'medium' },
        },
        MuiAppBar: {
            defaultProps: { elevation: 0 },
            styleOverrides: {
                colorPrimary: ({ theme: t }) => ({
                    background: `linear-gradient(90deg, ${t.palette.primary.dark} 0%, ${t.palette.primary.main} 50%, ${t.palette.primary.main} 100%)`,
                    borderBottom: `3px solid ${t.palette.secondary.main}`,
                }),
            },
        },
        MuiToolbar: {
            styleOverrides: {
                root: {
                    minHeight: 88,
                    '@media (min-width:600px)': { minHeight: 96 },
                    paddingLeft: 28,
                    paddingRight: 28,
                },
            },
        },
        MuiCard: {
            styleOverrides: {
                root: ({ theme: t }) => ({
                    border: `1px solid ${t.palette.divider}`,
                    boxShadow: '0 1px 2px rgba(16, 36, 24, 0.04)',
                }),
            },
        },
        MuiChip: {
            styleOverrides: {
                root: { fontWeight: 500 },
            },
        },
        MuiAlert: {
            styleOverrides: {
                root: { borderRadius: 12 },
            },
        },
        MuiDrawer: {
            styleOverrides: {
                paper: ({ theme: t }) => ({
                    backgroundColor: '#fbfaf5',
                    borderRight: `1px solid ${t.palette.divider}`,
                }),
            },
        },
        MuiTab: {
            styleOverrides: {
                root: { textTransform: 'none', fontWeight: 600 },
            },
        },
    },
});

theme = responsiveFontSizes(theme);

export default theme;
